export enum ModelStatus {
	Active = 'active',
	Inactive = 'inactive',
	Suspended = 'suspended',
	Evaluated = 'evaluated',
	Ended = 'ended',
	Extended = 'extended',
	Draft = 'draft',
	Planning = 'planning',
	Approved = 'approved',
	Pending = 'pending',
	Invalid = 'invalid',
	Rejected = 'rejected',
	Waived = 'waived',
}

export type StatusSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'primary' | 'contrast';

export interface StatusConfig {
	label: string;
	severity: StatusSeverity;
}

/**
 * Names of the entities that follow the unified i18n pattern
 * `<model>_status_<value>` (must match the BE `ModelStatusEnum::STATUS_MODELS`).
 *
 * `logbook_detail` is the per-registration validation outcome (pending/
 * approved/invalid/rejected/waived) of a single log book detail row — its
 * own model like any other here, sharing the same `ModelStatus.Waived`
 * case (added to the backend `ModelStatusEnum` too, even though most other
 * models never reach it).
 */
export type StatusModel = 'career' | 'logbook' | 'logbook_detail' | 'shift' | 'rotation' | 'suspension' | 'agreement';

/**
 * Severity is a property of the status value, not of the model:
 * `active` is always `success`, `ended` is always `secondary`, etc.
 */
export const STATUS_SEVERITY: Record<string, StatusSeverity> = {
	[ModelStatus.Active]: 'success',
	[ModelStatus.Inactive]: 'danger',
	[ModelStatus.Suspended]: 'warning',
	[ModelStatus.Evaluated]: 'success',
	[ModelStatus.Ended]: 'secondary',
	[ModelStatus.Extended]: 'primary',
	[ModelStatus.Draft]: 'warning',
	[ModelStatus.Planning]: 'info',
	[ModelStatus.Approved]: 'success',
	[ModelStatus.Pending]: 'info',
	[ModelStatus.Invalid]: 'warning',
	[ModelStatus.Rejected]: 'danger',
	[ModelStatus.Waived]: 'warning',
};

/**
 * Builds the StatusConfig map for a given entity, producing i18n keys
 * in the form `<model>_status_<value>` and resolving severities centrally.
 *
 * Pass `statuses` to restrict the map to a subset (e.g. only the values
 * actually emitted by the entity's BE accessor).
 */
/**
 * Returns the severity for a status value (model-agnostic).
 * Falls back to `secondary` for unknown values.
 */
export function getStatusSeverity(status: string | null | undefined): StatusSeverity {
	if (!status) return 'secondary';
	return STATUS_SEVERITY[status] ?? 'secondary';
}

export function buildStatusConfig(model: StatusModel, statuses?: ModelStatus[]): Record<string, StatusConfig> {
	const list = statuses ?? (Object.values(ModelStatus) as ModelStatus[]);
	return list.reduce<Record<string, StatusConfig>>((acc, status) => {
		acc[status] = {
			label: `${model}_status_${status}`,
			severity: STATUS_SEVERITY[status] ?? 'secondary',
		};
		return acc;
	}, {});
}
